import axios from "axios"
import appConfig from "../../configs/appConfig";
import {parseKendoGridDataState} from "../components/utils";

const comunicationPath = appConfig.API.comunications
const hazardReportPath = appConfig.API.hazard_reports
const weatherAlertPath = `${comunicationPath}/weather_alerts`


const getComunications = async (
	translateHook,
	i18n,
	dataState,
	exportScope = false,
	cancelDataToken = undefined,
	to_read = null,
	recipient_search = null,
	box = null
) => {

	let parseDataState,
        response_comunication,
		comunications
    if (exportScope)
		parseDataState = parseKendoGridDataState(dataState, '?skip=0')
	 else
        parseDataState = parseKendoGridDataState(dataState)

	if (to_read !== null)
		parseDataState += `&to_read=${to_read}`

	if (recipient_search)
		parseDataState += `&recipient_search=${encodeURIComponent(recipient_search)}`

	// Vista del rail Tutte/Inviate/Ricevute (all|sent|received); senza box
	// l'API mantiene il comportamento storico (contratto dell'app mobile)
	if (box)
		parseDataState += `&box=${box}`

	try {
		response_comunication = await axios.get(comunicationPath + parseDataState, cancelDataToken ? cancelDataToken : {})
		comunications = response_comunication.data.results
		return Promise.resolve({
			comunications,
			count: response_comunication.data.count
		})
	} catch (e) {
		return Promise.reject(new Error(`Exception Fetching comunication from API ${e}`))
	}
}

const getComunication = async (comunicationId) => {
	return axios.get(`${comunicationPath}/${comunicationId}`)
}

const createComunication = async (data, file) => {
	const formData = new FormData()
	formData.append('title', data.title)
	formData.append('html_content', data.html_content)
	formData.append('priority', data.priority)
	formData.append('requires_acknowledgement', data.requires_acknowledgement ?? false)
	;(data.recipient_ids || []).forEach((id) => formData.append('recipient_ids', id))
	if (file) formData.append('file', file)
	return axios.post(`${comunicationPath}`, formData)
}

const deleteComunication = async (comunicationId) => {
	return axios.delete(`${comunicationPath}/${comunicationId}`)
}

const submitComunicationToIride = async (comunicationId) => {
	return axios.post(`${hazardReportPath}/${comunicationId}/submit_to_iride`)
}

// ---- Generic-composer recipients (frozen contract, feature/326) -----------

// Facets are multi-value (OR within, AND across) -> arrays must serialize as
// repeated keys (profile=X&profile=Y), which FastAPI's List[...] Query expects.
// Axios' default array format (profile[]=X) would NOT bind, so serialize here.
const repeatParams = (params) => {
	const sp = new URLSearchParams()
	Object.entries(params || {}).forEach(([k, v]) => {
		if (v === undefined || v === null || v === "") return
		if (Array.isArray(v)) v.forEach((x) => sp.append(k, x))
		else sp.append(k, v)
	})
	return sp.toString()
}

// Paginated, filterable recipients listing -> {results, count}.
// params: {profile:[], default_region:[], company_id:[], group_id:[], search, skip, take}
const getComunicationRecipients = async (params) => {
	return axios.get(`${comunicationPath}/recipients`, {params, paramsSerializer: repeatParams})
}

// Select-all resolver: same filters, no paging -> {results:[user], count}.
// 422 above the server-side cap (5000).
const getComunicationRecipientIds = async (params) => {
	return axios.get(`${comunicationPath}/recipient_ids`, {params, paramsSerializer: repeatParams})
}

// Facet options WITH live counts (recompute as filters change) for the left rail.
// -> {profiles:[{value,count}], regions, groups:[{value,label,count}], companies}
const getComunicationRecipientFacets = async (params) => {
	return axios.get(`${comunicationPath}/recipient_facets`, {params, paramsSerializer: repeatParams})
}

// Unread communications for the current user (drives the mandatory gate).
const getUnreadComunications = async () => {
	const res = await axios.get(`${comunicationPath}?to_read=true`)
	return res.data?.results ?? res.data ?? []
}

// Count of unread mandatory communications (drives the gate poll/banner).
const getPendingAcknowledgementCount = async () => {
	const res = await axios.get(`${comunicationPath}/pending_acknowledgement_count`)
	return res.data?.count ?? 0
}

const getComunicationAcknowledgements = async (comunicationId) => {
	const response = await axios.get(`${comunicationPath}/${comunicationId}/acknowledgements`)
	return response.data
}

const acknowledgeComunication = async (comunicationId) => {
	return axios.post(`${comunicationPath}/${comunicationId}/acknowledge`)
}

// Bulk: marks every unread communication of the current user as read (single UPDATE).
const markAllComunicationsRead = async () => {
	return axios.post(`${comunicationPath}/read_all`)
}

// Authenticated attachment download (the file URLs need the Bearer header,
// so a plain <a href> won't work).
const downloadComunicationFile = async (comunicationId, filename) => {
	try {
		const response_ = await axios.get(
			`${comunicationPath}/${comunicationId}/files/${encodeURIComponent(filename)}`,
			{responseType: 'blob'}
		)
		return Promise.resolve(response_)
	} catch (err) {
		const blob = new Blob([err.response.data])
		const data = await blob.text()
		return Promise.reject({response: {data: JSON.parse(data)}})
	}
}

// Weather alerts (DPC-bulletin driven). `date` is an ISO date string (YYYY-MM-DD).
const getWeatherAlertPreview = async (date) => {
	return axios.get(`${weatherAlertPath}/dpc_preview`, {params: {date}})
}

const createWeatherAlert = async (data) => {
	return axios.post(weatherAlertPath, data)
}

// Publish a batch of region cards for a target day (one communication per card).
const createWeatherAlerts = async (data) => {
	return axios.post(weatherAlertPath, data)
}

// Weather alerts already published for a target day.
const getPublishedWeatherAlerts = async (date) => {
	return axios.get(weatherAlertPath, {params: {date}})
}

export {
	getComunication,
	getComunications,
	deleteComunication,
	createComunication,
	getComunicationRecipients,
	getComunicationRecipientIds,
	getComunicationRecipientFacets,
	downloadComunicationFile,
	getUnreadComunications,
	getPendingAcknowledgementCount,
	acknowledgeComunication,
	getComunicationAcknowledgements,
	markAllComunicationsRead,
	submitComunicationToIride,
	getWeatherAlertPreview,
	createWeatherAlert,
	createWeatherAlerts,
	getPublishedWeatherAlerts
}